import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Locale } from "@/i18n";
import { localePath, PRODUCT_FAMILIES } from "@/lib/nav";
import { ArrowUpRight, Instagram, Facebook, Youtube } from "lucide-react";
import { usePartnerModal } from "@/context/PartnerModalContext";
const logoWhite = "https://media.lacontessina.com/site/src/assets/logo-white.webp";
const FAMILY_LABEL_KEY: Record<string, string> = {
  classic: "style.classic",
  contemporary: "style.contemporary",
  textile: "style.textile",
};

export default function Footer() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language as Locale) || "it";
  const partner = usePartnerModal();

  return (
    <footer className="relative overflow-hidden bg-[hsl(0_0%_7%)] text-white/90 border-t border-white/10">
      <div className="relative lc-container-ext py-20 lg:py-28 z-10">
        {/* CTA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end pb-16 border-b border-white/10">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-white/55 before:content-[''] before:inline-block before:h-px before:w-8 before:bg-white/30">{t("common.eyebrow.lets_craft_together")}</span>
            <h2 className="mt-6 font-display font-light tracking-[-0.035em] leading-[0.98] text-[48px] md:text-[80px] lg:text-[112px] text-white">
              {t("footer.cta.become_a")}
              <span className="block italic font-serif font-normal">{t("footer.cta.partner")}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col lg:items-end gap-6">
            <p className="text-[15px] leading-[1.7] text-white/65 lg:text-right max-w-md">
              {t("footer.partner_description")}
            </p>
            <button
              type="button"
              onClick={partner.open}
              className="self-start lg:self-auto inline-flex items-center gap-3 rounded-full bg-white text-[hsl(0_0%_7%)] pl-7 pr-2 py-2 text-[14px] font-medium hover:bg-white/90 transition w-auto"
            >
              {t("button.become_partner")}
              <span className="h-11 w-11 rounded-full bg-[hsl(0_0%_7%)] text-white flex items-center justify-center">
                <ArrowUpRight className="h-5 w-5" />
              </span>
            </button>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 pt-16">
          <div className="col-span-2 md:col-span-4">
            <img
              src={logoWhite}
              alt="La Contessina"
              className="h-10 w-auto mb-6"
            />
            <p className="text-[14px] leading-[1.7] text-white/60 max-w-sm">
              {t("footer.company_description")}
            </p>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-white/45 mb-5">
              {t("footer.section.collections")}
            </h4>
            <ul className="space-y-3 text-[14px]">
              {PRODUCT_FAMILIES.map((family) => (
                <li key={family}>
                  <Link
                    to={localePath(locale, "products", family)}
                    className="text-white/75 hover:text-white transition"
                  >
                    {t(FAMILY_LABEL_KEY[family])}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-white/45 mb-5">
              {t("footer.section.company")}
            </h4>
            <ul className="space-y-3 text-[14px]">
              <li><Link to={localePath(locale, "about")} className="text-white/75 hover:text-white transition">{t("header.menu.about")}</Link></li>
              <li><Link to={localePath(locale, "projects")} className="text-white/75 hover:text-white transition">{t("header.menu.projects")}</Link></li>
              <li><Link to={localePath(locale, "catalogues")} className="text-white/75 hover:text-white transition">{t("header.menu.catalogs")}</Link></li>
              <li><Link to={localePath(locale, "stock")} className="text-white/75 hover:text-white transition">{t("header.menu.stock")}</Link></li>
              <li><Link to={localePath(locale, "news")} className="text-white/75 hover:text-white transition">{t("header.menu.news")}</Link></li>
              <li><Link to={localePath(locale, "contact")} className="text-white/75 hover:text-white transition">{t("header.menu.contact")}</Link></li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-4">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-white/45 mb-5">
              {t("footer.contacts_title")}
            </h4>
            <address className="not-italic text-[14px] leading-[1.8] text-white/75 space-y-2">
              <div>
                <a
                  href="https://maps.app.goo.gl/3wzVXwHvvaQxQx3H9"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="hover:text-white transition inline-flex items-center gap-1.5"
                >
                  Via Marna, 18, Marano di Parma (PR)
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
                </a>
              </div>
              <div>
                <a href="tel:+390521641060" className="hover:text-white transition">
                  +39 0521 641060
                </a>
              </div>
              <div>
                <a href="mailto:info@lacontessina.com" className="hover:text-white transition">
                  info@lacontessina.com
                </a>
              </div>
              <div className="text-white/50 text-[13px] pt-1">
                P.IVA 02690380346
              </div>
            </address>

            <div className="mt-6 flex items-center gap-2">
              <a href="https://flameplace.com/companies/mf/la-contessina" target="_blank" rel="noreferrer noopener" aria-label="Flameplace" className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-[hsl(0_0%_7%)] transition">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12.85,1.18C21.07,9.92 9.28,15.78 10.69,21.57C2.86,11.56 14.05,7.39 12.85,1.18Z" />
                  <path d="M20.49,8.25C20.94,10.6 20.04,12.96 18.77,14.9C18.06,16 17.2,16.97 16.35,17.96L11.97,23.08C9.32,16.86 23.86,11.21 14.48,1.46C17.51,2.45 19.89,5.12 20.49,8.25Z" />
                  <path d="M3.45,8.25C3.86,6.11 5.03,4.2 6.74,2.86C7.57,2.21 8.51,1.72 9.52,1.42C10.03,1.26 10.56,1.16 11.09,1.1C11.22,1.09 11.35,1.08 11.48,1.07C11.55,1.06 11.61,1.06 11.68,1.06C12.32,6.33 3.21,8.89 7.57,17.96C6.53,16.74 5.59,15.66 4.78,14.27C3.73,12.47 3.05,10.35 3.45,8.25Z" />
                </svg>
              </a>
              <a href="https://www.facebook.com/la.contessina.srl" target="_blank" rel="noreferrer noopener" aria-label="Facebook" className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-[hsl(0_0%_7%)] transition">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://www.instagram.com/la_contessina/" target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-[hsl(0_0%_7%)] transition">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://www.youtube.com/@LACONTESSINA" target="_blank" rel="noreferrer noopener" aria-label="YouTube" className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-[hsl(0_0%_7%)] transition">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Heritage lockup */}
        <div className="mt-14 pt-8 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] tracking-[0.3em] uppercase text-white/50">
          <span className="text-bronze">Est. 1968</span>
          <span className="opacity-40">·</span>
          <span>100% Made in Italy</span>
          <span className="opacity-40">·</span>
          <span>Marano di Parma</span>
        </div>
      </div>

      {/* Giant background wordmark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-12 md:bottom-16 flex items-end justify-center select-none"
      >
        <span
          className="font-display font-light text-white/[0.04] leading-none whitespace-nowrap"
          style={{ fontSize: "clamp(120px, 22vw, 360px)", letterSpacing: "-0.05em" }}
        >
          LA CONTESSINA
        </span>
      </div>

      <div className="relative z-10 border-t border-white/10">
        <div className="lc-container-ext py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[12px] text-white/50">
          <div>
            © {new Date().getFullYear()} La Contessina Srl — All rights reserved
            <span className="mx-2 opacity-50">·</span>
            Created with respect by{" "}
            <a
              href="https://cambia.dev/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition underline-offset-4 hover:underline"
            >
              Cambia.dev
            </a>
          </div>
          <div className="flex gap-6">
            <Link to={localePath(locale, "privacy")} className="hover:text-white transition">{t("footer.privacy_policy")}</Link>
            <Link to={localePath(locale, "cookies")} className="hover:text-white transition">{t("footer.cookie_policy")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
